import { Model } from "../../models/Model";
import { WorldRenderer } from "../WorldRenderer";
import { BaseService } from "./BaseService";
import * as THREE from "three";
import { WREvents } from "../WREvents";

export interface ObjectHandler {
  onClick(): void;
  onMouseMove(point: THREE.Vector3, buttons: number): void;
  convertToLocal(point: THREE.Vector3): THREE.Vector3;
}

export interface MouseEventCapture {
  loc: THREE.Vector2;
  time: number;
}

export enum UserEventType {
  Click = 'click',
  MouseMove = 'mousemove',
}

export class UserEventer extends BaseService {

  div!: HTMLCanvasElement;
  mouseCapture: MouseEventCapture = { loc: new THREE.Vector2(), time: 0 };
  public mouse3DLocation: THREE.Vector3 = new THREE.Vector3();
  private raycaster = new THREE.Raycaster();
  private mouseButtonDown = -1;
  public surfaceCursor = new THREE.Mesh(
    new THREE.SphereGeometry(1, 16, 16),
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.25 })
  );

  positionSelectAction?: (position: THREE.Vector3) => void;

  constructor(worldRenderer: WorldRenderer) {
    super(worldRenderer);
    this.worldRenderer.scene.add(this.surfaceCursor);
    this.surfaceCursor.visible = false; // Hide the cursor by default
    this.initEventListeners();
    // this.worldRenderer.onEvent(WREvents.SiteActivated, (site: SiteAnchorVisual) => {
    //   this.surfaceCursor.visible = true; // Show the cursor when a site is activated
    // });
    // this.worldRenderer.onEvent(WREvents.SiteDeactivated, (site: SiteAnchorVisual) => {
    //   this.surfaceCursor.visible = false; // Hide the cursor when no site is active
    // });
  }

  _selectedModel?: Model;
  public setSelectedModel(model: Model) {
    // throw new Error("Method not implemented.");
    this._selectedModel = model;
  }

  render(delta: number): void { }

  private initEventListeners() {
    // Example: Listen for mouse clicks on the renderer
    this.div = this.worldRenderer.renderer.domElement;
    this.div.addEventListener('mousedown', this.onMouseDown);
    this.div.addEventListener('mouseup', this.onMouseUp);
    this.div.addEventListener('mousemove', this.onMouseMove);
    this.div.addEventListener('keyup', (e) => this.onKeyUp(e.key));
    // this.div.addEventListener('keydown', this.onKeyDown);
    // this.div.addEventListener('wheel', this.onWheel);

  }

  onKeyUp(key: string) {
    if (key === 'Escape') {
      this.positionSelectAction = undefined; // Clear the position select action on Escape
      this._selectedModel = undefined; // Clear selected model on Escape
      this.surfaceCursor.visible = false; // Hide the cursor when Escape is pressed
    }
  }

  private onMouseDown = (event: MouseEvent) => {
    this.mouseButtonDown = event.button;
    if (event.button !== 0) {
      return;
    }
    this.mouseCapture = this.convertToScene(event);
  }

  private onMouseUp = (event: MouseEvent) => {
    this.mouseButtonDown = -1;
    if (event.button !== 0) {
      return;
    }
    const cap = this.convertToScene(event);
    const distance = this.mouseCapture.loc.distanceTo(cap.loc);
    const duration = cap.time - this.mouseCapture.time;
    if (distance < 0.1 && duration < 700) {
      const handler = this.getIntersect(UserEventType.Click);
      if (handler) {
        if (this._selectedModel) {
          this.worldRenderer.emitEvent(WREvents.ModelDeploy,
            this._selectedModel,
            this.mouse3DLocation
          );
          this._selectedModel = undefined; // Clear selected model after click
        } else if (this.positionSelectAction) {
          this.positionSelectAction(handler.convertToLocal(this.mouse3DLocation));
          this.positionSelectAction = undefined; // Clear action after use
        } else {
          handler.onClick();
        }
      }
    }
  }


  private _debounceTimeout: any = null;
  private onMouseMove = (event: MouseEvent) => {
    if (!this._selectedModel && !this.positionSelectAction) {
      this.surfaceCursor.visible = false; // Hide the cursor if no model is selected
      return;
    }
    let timeout = 500;
    if (this.worldRenderer.dataService.activeSite) {
      timeout = 1; // Shorter debounce for active site
    }
    if (this._debounceTimeout) {
      clearTimeout(this._debounceTimeout);
    }
    this._debounceTimeout = setTimeout(() => {
      this._debounceTimeout = null;
      this.mouseCapture = this.convertToScene(event);
      const handler = this.getIntersect(UserEventType.MouseMove);
      if (handler) {
        this.surfaceCursor.position.copy(this.mouse3DLocation);
        this.surfaceCursor.visible = true; // Show the cursor when mouse moves
        handler.onMouseMove(this.mouse3DLocation, this.mouseButtonDown);
      }
    }, timeout);
  }

  private convertToScene(event: MouseEvent): MouseEventCapture {
    const clientWidth = this.div.clientWidth;
    const clientHeight = this.div.clientHeight;
    const clientX = event.clientX - this.div.getBoundingClientRect().left;
    const clientY = event.clientY - this.div.getBoundingClientRect().top;
    const x = (clientX / clientWidth) * 2 - 1;
    const y = - (clientY / clientHeight) * 2 + 1;
    return {
      loc: new THREE.Vector2(x, y),
      time: Date.now()
    }
  }

  private getIntersect(eventType: UserEventType): ObjectHandler | null {
    let targets: THREE.Object3D[] = this.worldRenderer.scene.children;
    if (this.worldRenderer.dataService.activeSite) {
      targets = this.worldRenderer.dataService.activeSite.getEventerTargets(eventType);
    }
    this.raycaster.setFromCamera(this.mouseCapture.loc, this.worldRenderer.camera);
    let intersects = this.raycaster.intersectObjects(targets, true);
    if (intersects.length === 0) {
      return null;
    }
    const cameraPosition = new THREE.Vector3();
    this.worldRenderer.cameraControls.getPosition(cameraPosition);

    intersects = intersects.filter(intersect => {
      return !(intersect.object instanceof THREE.GridHelper || intersect.object instanceof THREE.AxesHelper) &&
        intersect.object !== this.surfaceCursor && intersect.object.visible;
    });

    intersects.sort((a, b) => {
      const distanceA = a.point.distanceTo(cameraPosition);
      const distanceB = b.point.distanceTo(cameraPosition);
      return distanceA - distanceB;
    });
    const closest = intersects[0];
    if (!closest) {
      return null;
    }
    // console.log('Closest intersection:', closest);
    const handler = this.findHandler(closest.object);
    if (handler) {
      this.mouse3DLocation.copy(closest.point);
      return handler;
    }
    return null;
  }

  private findHandler(object: THREE.Object3D): ObjectHandler | null {
    if (object.userData && object.userData.handler) {
      return object.userData.handler as ObjectHandler;
    }
    if (object.parent) {
      const handler = this.findHandler(object.parent);
      if (handler) {
        return handler;
      }
    }
    return null;
  }
}